import { useMemo } from 'react'

import carrierUtils, { SegmentedData } from '@lib/carrier'
import { useTranslation } from '@lib/i18n'
import { getValidity } from '@pages/Checkout/BookingDetails/TripDetails/ConnectionDetails/utils'

interface UseValidityHookReturn {
  outboundValidityMessage: string | null
  inboundValidityMessage: string | null
  getValidityRules: (fareClass: string, connectionType: ConnectionType) => TicketValidityRule[]
}

interface UseValidityProps {
  fareClass?: string
  carriers: BaseCarrier[]
  outbound: SegmentedData | null
  inbound: SegmentedData | null
}

const getCarrierValidityRules = (
  selectedFareClass: string,
  carriers: BaseCarrier[],
  ruleType: ConnectionType,
): TicketValidityRule[] => {
  return carriers.reduce((allRules, carrier) => {
    const matchingFareClass = carrier.fareClasses.find(fareClass => fareClass.code === selectedFareClass)
    if (matchingFareClass && matchingFareClass.ticketValidityRules?.length) {
      return allRules.concat(matchingFareClass.ticketValidityRules.filter(rule => rule.ruleType === ruleType))
    }
    return allRules
  }, [] as TicketValidityRule[])
}

const useValidity = ({ fareClass, carriers, inbound, outbound }: UseValidityProps): UseValidityHookReturn => {
  const { t } = useTranslation()

  const outboundCarriers = useMemo(() => carrierUtils.getCarriers(outbound, carriers), [carriers, outbound])
  const inboundCarriers = useMemo(() => carrierUtils.getCarriers(inbound, carriers), [carriers, inbound])

  const outboundValidityMessage = useMemo(
    () => getValidity(outboundCarriers, fareClass, 'outbound'),
    [fareClass, outboundCarriers],
  )
  const inboundValidityMessage = useMemo(
    () => getValidity(inboundCarriers, fareClass, 'inbound'),
    [fareClass, inboundCarriers],
  )

  const isBothValid = inbound ? !!outboundValidityMessage && outboundValidityMessage === inboundValidityMessage : false

  return {
    outboundValidityMessage: isBothValid ? null : outboundValidityMessage,
    inboundValidityMessage: isBothValid
      ? t('checkout.tripDetails.availableBoth', { message: inboundValidityMessage })
      : inboundValidityMessage,
    getValidityRules: (fareClass: string, connectionType: ConnectionType = 'outbound') => {
      const carriers = connectionType === 'outbound' ? outboundCarriers : inboundCarriers
      return getCarrierValidityRules(fareClass, carriers, connectionType)
    },
  }
}

export default useValidity
